import {translateObject} from '@ngneat/transloco';
import { QuestAvailabilityTypes, QuestDifficulty, RecommendedFor } from '../interfaces/quest-info.enums';
import { datetimeToPrettyString, normalizeDate, secondsToTime, toEUDateString } from './date.functions';
import { QuestAvailability, QuestInfoParagraph } from '../interfaces/quest-info.interfaces';
import {
  QuestMetadata,
  QuestPublicationRequest,
  QuestStepFunctionalMetadata
} from '../interfaces/quest-core.interfaces';
import { Dict, TimeSpan } from '../interfaces/common.interfaces';
import { Organization } from '../interfaces/user.interfaces';

export const questAvailabilityToText = (availability: QuestAvailability): string => {
  if (!availability || !availability.type) {
    return translateObject('availability.always') as string;
  }

  switch (availability.type) {
    case QuestAvailabilityTypes.always:
      return translateObject('availability.always') as string;
    case QuestAvailabilityTypes.unavailable:
      return translateObject('availability.unavailable') as string;
    case QuestAvailabilityTypes.fromDate:
      return `${translateObject('availability.from')} ${toEUDateString(normalizeDate(availability.data.fromDate))}`;
    case QuestAvailabilityTypes.toDate:
      return `${translateObject('availability.to')} ${toEUDateString(normalizeDate(availability.data.toDate))}`;
    case QuestAvailabilityTypes.fromDateToDate:
      return `${toEUDateString(normalizeDate(availability.data.fromDate))} -
        ${toEUDateString(normalizeDate(availability.data.toDate))}`;
    case QuestAvailabilityTypes.expiresAt:
      return `${translateObject('availability.expiresAt')} ${datetimeToPrettyString(normalizeDate(availability.data.expiresAt))}`;
    case QuestAvailabilityTypes.today:
      return translateObject('today') as string;
    default:
      return '';
  }
};

export const difficultyEnumToPrettyText = (difficulty: QuestDifficulty): string => {
  switch (difficulty) {
    case QuestDifficulty.easy: return translateObject('easy') as string;
    case QuestDifficulty.medium: return translateObject('medium') as string;
    case QuestDifficulty.hard: return translateObject('hard') as string;
    case QuestDifficulty.master: return translateObject('master') as string;
    default: return '';
  }
}

export const transformQuestFromServer = (raw: Dict<any>, urlRoot: string): QuestMetadata => {
  const splitLP = raw['location_point']?.split(',');

  const rating = {
    value: (raw['positive_votes'] / raw['all_votes']) * 100,
    voters: raw['all_votes'],
    myVote: raw['cur_user_vote_id'] ? {value: raw['cur_user_vote'], voteId: raw['cur_user_vote_id']} : undefined
  };

  let images = [];

  if (Array.isArray(raw['images'])) {
    images = raw['images'].map(value => {
      let id = undefined;
      let description = undefined;
      if (typeof value !== 'string') {
        id = value.id;
        description = value.description;
        value = value.url;
      }

      return {id, url: value.includes('/') ? value : `${urlRoot}/images/${value}.jpg`, description};
    });
  }

  // if (raw['images']) {
  //   images.push({url: `${this.env.environment.API_URL}/preview/${raw['preview']}.png`});
  // }

  const steps = !raw.hasOwnProperty('steps') ? [] : raw['steps'].map((value: any) => {
    const functionalMetadata: QuestStepFunctionalMetadata = value.functional_metadata;

    if (!functionalMetadata.clueMetadata) {
      functionalMetadata.clueMetadata = [];
    }

    let stepImages = [];

    if (value.images) {
      stepImages = value.images.map((img) => {
        let id = undefined;
        let description = undefined;
        if (typeof img !== 'string') {
          id = img.id;
          description = img.description
          img = img.url;
        }

        return {
          id,
          name: img.split('/')[-1],
          url: img.includes('/') ? img : `${urlRoot}/images/${img}.jpg`,
          description
        }
      });
    }

    value.functional_metadata['images'] = stepImages;

    return {
      id: value.id,
      name: value.name,
      actionZone: value.action_zone.connections ? value.action_zone : null,
      functionalMetadata: value.functional_metadata,
      finalDescription: value.final_description,
    };
  });

  let approxTime: TimeSpan = typeof raw['approximate_time'] === 'number' ? secondsToTime(raw['approximate_time']) : raw['approximate_time'];
  if (approxTime && !approxTime.hours && !approxTime.minutes && !approxTime.seconds) {
    approxTime = undefined;
  }

  let publicationRequest: QuestPublicationRequest = null;
  if (raw['publication_requests'] && raw['publication_requests'].length > 0) {
    publicationRequest = {
      id: raw['publication_requests'][0].id,
      status: raw['publication_requests'][0].status,
      createdAt: new Date(raw['publication_requests'][0]['created_at']),
      updatedAt: new Date(raw['publication_requests'][0]['updated_at'])
    };
  }

  let organization: Organization = null;

  if (raw['organization']) {
    organization = {
      id: raw['organization'].id,
      name: raw['organization'].name,
      description: raw['organization'].description,
      secondaryLogo: raw['organization'].secondary_logo,
      primaryColor: raw['organization'].primary_color,
      secondaryColor: raw['organization'].secondary_color,
      type: raw['organization'].type,
    };
  }

  const quest: QuestMetadata = {
    id: raw['id'],
    name: raw['name'],
    description: transformDescription(raw),
    createdAt: new Date(raw['created_at']),
    updatedAt: new Date(raw['updated_at']),
    creator: {
      email: raw['creator_email'],
      username: raw['creator_username'],
      firstName: raw['creator_first_name'],
      lastName: raw['creator_last_name'],
      isSuperUser: raw['creator_is_superuser']
    },
    organization,
    teamMode: raw['team_mode'],
    competitiveMode: raw['competitive_mode'],
    adaptive: raw['adaptive'],
    locationPoint: splitLP
      ? {
        lat: Number(splitLP[0]),
        lng: Number(splitLP[1])
      }
      : undefined,
    isDraft: raw['is_draft'],
    config: raw['config'],
    publicationRequest,
    steps,
    keywords: raw['keywords'],
    generated: raw['generated'],
    info: {
      approxTime,
      approxDistance: raw['approx_distance'],
      approved: raw['approved'],
      address: raw['address'],
      images,
      difficulty: raw['difficulty'] ? QuestDifficulty[raw['difficulty']] : null,
      rewards: {
        exp: raw['rewards_exp']
      },
      rating,
      public: raw.hasOwnProperty('public') ? raw['public'] : !raw['is_private'],
      recommendedFor: raw['recommended_for'] ? raw['recommended_for'] : RecommendedFor.walk,
      availability: raw['availability']
        ? {
          type: raw['availability'].type,
          data: raw['availability'].data
        }
        : undefined,
      tags: raw['tags']
    },
    gameAccess: !raw['game_access'] ? null : raw['game_access'].map((value: any) => ({
      id: value.id,
      accessType: value.access_type,
      config: value.config
    })),
    accessLevel: raw['access_level'],
  };

  // we don't need (for now at least) to transform the dates to Date
  // if (quest.info.availability && quest.info.availability.data) {
  //   if (typeof quest.info.availability.data.fromDate === 'string' || typeof quest.info.availability.data.fromDate === 'number') {
  //     quest.info.availability.data.fromDate = new Date(quest.info.availability.data.fromDate);
  //   } else if (quest.info.availability.data.fromDate) {
  //     quest.info.availability.data.fromDate = new Date(
  //       quest.info.availability.data.fromDate.year,
  //       quest.info.availability.data.fromDate.month,
  //       quest.info.availability.data.fromDate.day
  //     );
  //   }
  //
  //   if (typeof quest.info.availability.data.toDate === 'string' || typeof quest.info.availability.data.toDate === 'number') {
  //     quest.info.availability.data.toDate = new Date(quest.info.availability.data.toDate);
  //   } else if (quest.info.availability.data.toDate) {
  //     quest.info.availability.data.toDate = new Date(
  //       quest.info.availability.data.toDate.year,
  //       quest.info.availability.data.toDate.month,
  //       quest.info.availability.data.toDate.day
  //     );
  //   }
  // }

  return quest;
}

export const transformDescription = (raw: any): QuestInfoParagraph[] => {
  if (typeof raw.description === 'string') {
    return [{data: raw.description, type: 'text'}];
  }

  return raw.description;
}
